// src/pages/IngestionPage.jsx — v8.7 CANON
// Data Ingestion Layer v1.2 UI
// CSV upload → preview → import → status

import { useRef, useState } from "react";
import SectionTitle        from "../components/ui/SectionTitle";
import Panel               from "../components/ui/Panel";
import { useIngestionUpload, useIngestionStatus } from "../api/hooks/useIngestion";
import { BASE_URL } from "../api/workerClient";

/* ── Shared styles ─────────────────────────────────── */
const mono = { fontFamily: "var(--font-mono)" };

const badge = (color, bg) => ({
  display:       "inline-flex",
  alignItems:    "center",
  padding:       "2px 8px",
  borderRadius:  "var(--radius-sm)",
  background:    bg,
  border:        `1px solid ${color}30`,
  fontFamily:    "var(--font-mono)",
  fontSize:      9,
  fontWeight:    700,
  letterSpacing: "0.1em",
  color,
  textTransform: "uppercase",
});

const Divider = ({ label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 16, margin: "4px 0" }}>
    <div style={{ height: 1, flex: 1, background: "var(--border-subtle)" }} />
    {label && <span style={{ ...mono, fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--text-muted)", flexShrink: 0 }}>{label}</span>}
    <div style={{ height: 1, flex: 1, background: "var(--border-subtle)" }} />
  </div>
);

/* ── Status Section ───────────────────────────────── */
function IngestionStatus() {
  const { data, loading, refresh } = useIngestionStatus();

  const byStatus = data?.by_status ?? [];
  const batches  = data?.last_batches ?? [];

  const statusColors = {
    pending:    "var(--warning)",
    processing: "var(--accent-primary)",
    done:       "var(--success)",
    error:      "var(--danger)",
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <SectionTitle title="Ingestion Queue" />
        <button
          onClick={refresh}
          style={{ ...mono, fontSize: 10, color: "var(--accent-primary)", background: "none", border: "none", cursor: "pointer", letterSpacing: "0.08em" }}
        >
          ↻ REFRESH
        </button>
      </div>

      {loading ? (
        <div style={{ ...mono, fontSize: 12, color: "var(--text-muted)", padding: "16px 0" }}>Loading…</div>
      ) : (
        <>
          {/* Status counters */}
          <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
            {byStatus.length === 0 ? (
              <span style={{ ...mono, fontSize: 11, color: "var(--text-muted)" }}>No events in queue</span>
            ) : (
              byStatus.map(s => (
                <div key={s.status} style={{
                  padding:       "10px 16px",
                  background:    "var(--bg-card)",
                  border:        "1px solid var(--border-subtle)",
                  borderRadius:  "var(--radius)",
                  display:       "flex",
                  flexDirection: "column",
                  gap:           4,
                  minWidth:      90,
                }}>
                  <div style={{ ...mono, fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-muted)" }}>
                    {s.status}
                  </div>
                  <div style={{ ...mono, fontSize: 22, fontWeight: 700, color: statusColors[s.status] ?? "var(--text-primary)" }}>
                    {s.count}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Last batches */}
          {batches.length > 0 && (
            <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius)", overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Batch ID", "Source", "Total", "Done", "Errors"].map(h => (
                      <th key={h} style={{ ...mono, padding: "9px 14px", fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-tertiary)", fontWeight: 400, textAlign: "left", borderBottom: "1px solid var(--border-subtle)", background: "var(--bg-tertiary)" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b, i) => (
                    <tr key={b.ingestion_batch_id} style={{ borderBottom: i < batches.length - 1 ? "1px solid var(--border-subtle)" : "none" }}>
                      <td style={{ ...mono, padding: "9px 14px", fontSize: 11, color: "var(--text-secondary)", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {b.ingestion_batch_id}
                      </td>
                      <td style={{ ...mono, padding: "9px 14px", fontSize: 11, color: "var(--accent-primary)" }}>{b.source}</td>
                      <td style={{ ...mono, padding: "9px 14px", fontSize: 11, color: "var(--text-primary)" }}>{b.total}</td>
                      <td style={{ ...mono, padding: "9px 14px", fontSize: 11, color: "var(--success)" }}>{b.done}</td>
                      <td style={{ ...mono, padding: "9px 14px", fontSize: 11, color: b.errors > 0 ? "var(--danger)" : "var(--text-muted)" }}>{b.errors}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── CSV Upload ───────────────────────────────────── */
function CSVUpload() {
  const fileRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const { preview, importing, result, error, step, previewFile, importData, reset } = useIngestionUpload();

  const handleFile = (file) => {
    if (!file) return;
    if (!file.name.endsWith(".csv")) {
      alert("Please upload a .csv file");
      return;
    }
    previewFile(file);
  };

  const dropZoneStyle = {
    border:        `2px dashed ${dragging ? "var(--accent-primary)" : "var(--border-subtle)"}`,
    borderRadius:  "var(--radius)",
    padding:       "40px 24px",
    textAlign:     "center",
    cursor:        "pointer",
    transition:    "border-color var(--t-fast), background var(--t-fast)",
    background:    dragging ? "var(--accent-faint)" : "var(--bg-card)",
  };

  return (
    <div>
      <SectionTitle title="CSV Import" />

      {/* Format hint */}
      <div style={{ ...mono, fontSize: 10, color: "var(--text-muted)", marginBottom: 16, padding: "10px 14px", background: "var(--bg-subtle)", borderRadius: "var(--radius)", border: "1px solid var(--border-subtle)" }}>
        Required columns: <span style={{ color: "var(--accent-primary)" }}>date</span>, <span style={{ color: "var(--success)" }}>spend</span>, <span style={{ color: "var(--success)" }}>revenue</span> &nbsp;·&nbsp;
        Optional: campaign, clicks, conversions, currency, id
      </div>

      {/* Drop zone */}
      {step === "idle" || step === "parsed" ? (
        <div
          style={dropZoneStyle}
          onClick={() => fileRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            style={{ display: "none" }}
            onChange={e => handleFile(e.target.files[0])}
          />
          <div style={{ ...mono, fontSize: 28, marginBottom: 8, opacity: 0.3 }}>⬆</div>
          <div style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 4 }}>
            Drop a .csv file here, or click to browse
          </div>
          {step === "parsed" && (
            <div style={{ ...mono, fontSize: 10, color: "var(--accent-primary)", marginTop: 8 }}>
              Loading preview…
            </div>
          )}
        </div>
      ) : null}

      {/* Error */}
      {error && (
        <div style={{ ...mono, fontSize: 12, color: "var(--danger)", padding: "12px 16px", background: "var(--danger-bg)", borderRadius: "var(--radius)", border: "1px solid var(--danger-border)", marginTop: 12 }}>
          ✗ {error}
        </div>
      )}

      {/* Preview */}
      {preview && step === "previewed" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 4 }}>
          {/* Summary */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {[
              { label: "Total rows", value: preview.total,   color: "var(--text-primary)" },
              { label: "Valid",      value: preview.valid,   color: "var(--success)" },
              { label: "Invalid",    value: preview.invalid, color: preview.invalid > 0 ? "var(--danger)" : "var(--text-muted)" },
            ].map(item => (
              <div key={item.label} style={{ padding: "10px 16px", background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius)" }}>
                <div style={{ ...mono, fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 4 }}>{item.label}</div>
                <div style={{ ...mono, fontSize: 22, fontWeight: 700, color: item.color }}>{item.value}</div>
              </div>
            ))}
          </div>

          {/* Issues */}
          {preview.invalid > 0 && preview.issues?.length > 0 && (
            <div style={{ ...mono, fontSize: 11, color: "var(--warning)", padding: "10px 14px", background: "var(--warning-bg)", borderRadius: "var(--radius)", border: "1px solid var(--warning-border)" }}>
              ⚠ {preview.issues.length} rows with errors: {preview.issues.slice(0, 3).map(i => `row ${i.index + 1}: ${i.issues?.join(", ")}`).join(" · ")}
            </div>
          )}

          {/* Preview table */}
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius)", overflow: "hidden" }}>
            <div style={{ ...mono, fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--text-muted)", padding: "10px 16px", borderBottom: "1px solid var(--border-subtle)", background: "var(--bg-tertiary)" }}>
              Preview (first 10 rows)
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["#", "Date", "Campaign", "Spend", "Revenue", "Clicks", "Valid"].map(h => (
                      <th key={h} style={{ ...mono, padding: "8px 12px", fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-tertiary)", fontWeight: 400, textAlign: "left", borderBottom: "1px solid var(--border-subtle)" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.preview?.slice(0, 10).map((row, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid var(--border-subtle)", background: !row.valid ? "var(--danger-bg)" : "transparent" }}>
                      <td style={{ ...mono, padding: "7px 12px", fontSize: 10, color: "var(--text-muted)" }}>{row.index + 1}</td>
                      <td style={{ ...mono, padding: "7px 12px", fontSize: 11, color: "var(--text-secondary)" }}>{row.parsed?.timestamp ? new Date(row.parsed.timestamp * 1000).toISOString().slice(0, 10) : "—"}</td>
                      <td style={{ ...mono, padding: "7px 12px", fontSize: 11, color: "var(--text-tertiary)" }}>{row.parsed?.campaign ?? "—"}</td>
                      <td style={{ ...mono, padding: "7px 12px", fontSize: 11, color: "var(--danger)" }}>€ {row.parsed?.spend?.toFixed(0) ?? 0}</td>
                      <td style={{ ...mono, padding: "7px 12px", fontSize: 11, color: "var(--success)" }}>€ {row.parsed?.revenue?.toFixed(0) ?? 0}</td>
                      <td style={{ ...mono, padding: "7px 12px", fontSize: 11, color: "var(--text-tertiary)" }}>{row.parsed?.clicks ?? 0}</td>
                      <td style={{ padding: "7px 12px" }}>
                        <span style={badge(row.valid ? "var(--success)" : "var(--danger)", row.valid ? "var(--success-bg)" : "var(--danger-bg)")}>
                          {row.valid ? "OK" : "ERR"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: 12 }}>
            <button
              onClick={() => importData(preview.items, "csv")}
              disabled={importing || !preview.ready_to_import}
              style={{
                padding:       "10px 24px",
                borderRadius:  "var(--radius)",
                background:    preview.ready_to_import ? "var(--accent-primary)" : "var(--bg-elevated)",
                border:        "none",
                cursor:        preview.ready_to_import ? "pointer" : "not-allowed",
                ...mono,
                fontSize:      12,
                fontWeight:    700,
                letterSpacing: "0.08em",
                color:         preview.ready_to_import ? "#fff" : "var(--text-muted)",
                transition:    "background var(--t-fast)",
              }}
            >
              {importing ? "IMPORTING…" : `IMPORT ${preview.valid} ROWS`}
            </button>
            <button
              onClick={reset}
              style={{ padding: "10px 16px", borderRadius: "var(--radius)", background: "var(--bg-card)", border: "1px solid var(--border-subtle)", cursor: "pointer", ...mono, fontSize: 11, color: "var(--text-tertiary)" }}
            >
              CANCEL
            </button>
          </div>
        </div>
      )}

      {/* Result */}
      {result && step === "imported" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 4 }}>
          <div style={{
            padding:      "16px 20px",
            background:   result.ok ? "var(--success-bg)" : "var(--danger-bg)",
            border:       `1px solid ${result.ok ? "var(--success-border)" : "var(--danger-border)"}`,
            borderRadius: "var(--radius)",
          }}>
            <div style={{ ...mono, fontSize: 13, fontWeight: 700, color: result.ok ? "var(--success)" : "var(--danger)", marginBottom: 10 }}>
              {result.ok ? "✓ Import successful" : "✗ Import failed"}
            </div>
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
              {[
                { label: "Received",    value: result.items_received },
                { label: "Written",     value: result.items_written_raw,  color: "var(--success)" },
                { label: "Duplicates",  value: result.duplicates_skipped, color: "var(--warning)" },
                { label: "Errors",      value: result.errors?.length ?? 0, color: "var(--danger)" },
              ].map(item => (
                <div key={item.label}>
                  <div style={{ ...mono, fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 3 }}>{item.label}</div>
                  <div style={{ ...mono, fontSize: 18, fontWeight: 700, color: item.color ?? "var(--text-primary)" }}>{item.value ?? "—"}</div>
                </div>
              ))}
            </div>
            {result.ok && (
              <div style={{ ...mono, fontSize: 10, color: "var(--text-tertiary)", marginTop: 10 }}>
                Batch: {result.ingestion_batch_id} · Status: queued for normalization
              </div>
            )}
          </div>
          <button
            onClick={reset}
            style={{ alignSelf: "flex-start", padding: "8px 16px", borderRadius: "var(--radius)", background: "var(--bg-card)", border: "1px solid var(--border-subtle)", cursor: "pointer", ...mono, fontSize: 11, color: "var(--text-secondary)" }}
          >
            IMPORT MORE
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Webhook Info ─────────────────────────────────── */
function WebhookInfo() {
  const endpoint = `${BASE_URL}/api/ingest/webhook/keitaro`;

  return (
    <div>
      <SectionTitle title="Webhook Integration" />
      <Panel>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <div style={{ ...mono, fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 6 }}>Endpoint</div>
            <code style={{ ...mono, fontSize: 11, color: "var(--accent-primary)", background: "var(--bg-tertiary)", padding: "6px 10px", borderRadius: "var(--radius-sm)", display: "block" }}>
              POST /api/ingest/webhook/:provider
            </code>
          </div>
          <div>
            <div style={{ ...mono, fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 6 }}>Supported Providers</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {["keitaro", "binom", "redtrack", "voluum", "custom"].map(p => (
                <span key={p} style={badge("var(--accent-primary)", "var(--accent-faint)")}>{p}</span>
              ))}
            </div>
          </div>
          <div>
            <div style={{ ...mono, fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 6 }}>Signature Header</div>
            <code style={{ ...mono, fontSize: 11, color: "var(--text-secondary)", background: "var(--bg-tertiary)", padding: "6px 10px", borderRadius: "var(--radius-sm)", display: "block" }}>
              X-VIRAJ-SIGNATURE: sha256=&lt;hmac&gt;
            </code>
          </div>
          <div style={{ ...mono, fontSize: 10, color: "var(--text-tertiary)" }}>
            Webhook secret is set via Cloudflare secret: <span style={{ color: "var(--accent-primary)" }}>WEBHOOK_SECRET</span>
          </div>
        </div>
      </Panel>
    </div>
  );
}

/* ── MAIN PAGE ────────────────────────────────────── */
export default function IngestionPage() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20 }}>
        <div>
          <SectionTitle title="Data Ingestion" />
          <div style={{ fontSize: 13, color: "var(--text-tertiary)", marginTop: 4, maxWidth: 520, lineHeight: 1.6 }}>
            Upload campaign spend and revenue data. The system normalizes it and runs risk analysis.
          </div>
        </div>
        <span style={badge("var(--accent-primary)", "var(--accent-faint)")}>Layer v1.2</span>
      </div>

      <Divider label="CSV Import" />
      <CSVUpload />

      <Divider label="Queue Status" />
      <IngestionStatus />

      <Divider label="Webhook" />
      <WebhookInfo />
    </div>
  );
}
